import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
  useGridApiContext,
} from "@mui/x-data-grid";
import {
  InspectionListQuery,
  InspectionResult,
  TeamKind,
  useInspectionDetailsUpdateMutation,
} from "../../../../__generated__/types-and-hooks";
import { useTeam } from "../../TeamLayout";
import { useCallback, useMemo } from "react";
import { Chip, Tooltip } from "@mui/joy";
import { PSIValue } from "../../../../components/inspections/PSI";
import { InlineValue } from "../../../../components/inspections/Inline";
import { FAValue } from "../../../../components/inspections/FA";
import { ServiceTypeIcon } from "../../../../components/ServiceTypeIcon";
import InspectionRef from "../../../../components/InspectionRef";
import { AgencyAutocomplete } from "../../../../components/AgencyAutocomplete";
import { SupplierAutocomplete } from "../../../../components/SupplierAutocomplete";
import SupplierTooltip from "./SupplierTooltip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GridStyles from "../../../../components/datagrid/GridStyles";

export type TableViewInspections = any;

export interface TableViewProps {
  inspections: TableViewInspections[];
  loading: boolean;
}

type GQLInspectionType = NonNullable<
  InspectionListQuery["inspection_list"]
>["inspections"][number];

export default function TableView({ inspections, loading }: TableViewProps) {
  const { team } = useTeam();
  const [inspectionUpdateMutation] = useInspectionDetailsUpdateMutation({});

  function RefRenderCell(
    params: GridRenderCellParams<
      GQLInspectionType,
      GQLInspectionType["uniqueId"]
    >
  ) {
    return (
      <InspectionRef inspectionRef={params.row.uniqueId} teamSlug={team.slug} />
    );
  }

  function ServiceTypeRenderCell(
    params: GridRenderCellParams<
      GQLInspectionType,
      GQLInspectionType["serviceType"]
    >
  ) {
    return <ServiceTypeIcon value={params.value ?? "PSI"} />;
  }

  // function VisibilityRenderCell(
  //   params: GridRenderCellParams<
  //     GQLInspectionType,
  //     GQLInspectionType["visibility"]
  //   >
  // ) {
  //   return (
  //     <Chip
  //       size={"sm"}
  //       sx={{ borderRadius: 4 }}
  //       startDecorator={
  //         <>
  //           {params.value === "PUBLIC" && <PublicIcon />}
  //           {params.value === "ASSIGNED" && <AssignedIcon />}
  //           {params.value === "PRIVATE" && <PrivateIcon />}
  //         </>
  //       }
  //     >
  //       {params.value}
  //     </Chip>
  //   );
  // }

  function ResultRenderCell(
    params: GridRenderCellParams<GQLInspectionType, GQLInspectionType["result"]>
  ) {
    if (!params.value) return null;

    return (
      <Chip
        size={"sm"}
        sx={{ borderRadius: 4 }}
        startDecorator={
          <>
            {params.value === InspectionResult.Pass && (
              <CheckCircleIcon color="success" />
            )}
            {params.value === InspectionResult.Fail && (
              <CancelIcon color="error" />
            )}
            {params.value === InspectionResult.Inconclusive && (
              <CancelIcon color="error" />
            )}
          </>
        }
      >
        {params.value}
      </Chip>
    );
  }

  function StatusRenderCell(
    params: GridRenderCellParams<GQLInspectionType, GQLInspectionType["status"]>
  ) {
    const statusStylesMap = {
      DRAFT: { backgroundColor: "#bdbdbd", color: "#000" }, // Grey
      PENDING: { backgroundColor: "#ffb74d", color: "#000" }, // Light orange
      CONFIRMED: { backgroundColor: "#0d47a1", color: "#fff" }, // Dark blue
      CANCELLED: { backgroundColor: "#d32f2f", color: "#fff" }, // Red
      INPROGRESS: { backgroundColor: "#42a5f5", color: "#000" }, // Light blue
      COMPLETED: { backgroundColor: "#388e3c", color: "#fff" }, // Green
    };

    const status = params.value as keyof typeof statusStylesMap;
    const chipStyles = statusStylesMap[status] || {
      backgroundColor: "#e0e0e0",
      color: "#000",
    };
    return (
      <Chip size={"sm"} sx={{ ...chipStyles, borderRadius: 4 }}>
        {status}
      </Chip>
    );
  }

  // function SamplesRenderCell(
  //   params: GridRenderCellParams<
  //     GQLInspectionType,
  //     GQLInspectionType["samples"]
  //   >
  // ) {
  //   if (!params.row.samples || params.row.samples.length === 0) {
  //     return (
  //       <>
  //         42 SKUs{" "}
  //         <IconButton
  //           sx={{ fontSize: 12, p: 0, m: 0, "--IconButton-size": "26px" }}
  //           size="sm"
  //           variant={"outlined"}
  //         >
  //           <CategoryIcon />
  //         </IconButton>
  //       </>
  //     );
  //   }

  //   return params.row.samples?.map((sample) => sample.sku).join(", ");
  // }

  function BrandRenderCell(
    params: GridRenderCellParams<GQLInspectionType, GQLInspectionType["brand"]>
  ) {
    if (!params.row.brand) return null;

    return (
      <>
        <Chip size={"sm"} sx={{ borderRadius: 4 }}>
          {params.row.brand?.name}
        </Chip>
      </>
    );
  }

  function AgencyRenderCell(
    params: GridRenderCellParams<GQLInspectionType, GQLInspectionType["agency"]>
  ) {
    if (!params.row.agency) return null;

    return (
      <>
        <Chip size={"sm"} sx={{ borderRadius: 4 }}>
          {params.row.agency?.name}
        </Chip>
      </>
    );
  }

  function AgencyEditCell(props: GridRenderEditCellParams) {
    const { id, value, field, hasFocus } = props;
    const apiRef = useGridApiContext();

    return (
      <AgencyAutocomplete
        hasFocus={hasFocus}
        agency={value}
        onChange={(agency) => {
          apiRef.current.setEditCellValue({
            id,
            field,
            value: {
              ...agency,
            },
          });
          apiRef.current.stopCellEditMode({ id, field });
        }}
      />
    );
  }

  function SupplierRenderCell(
    params: GridRenderCellParams<
      GQLInspectionType,
      GQLInspectionType["supplier"]
    >
  ) {
    if (!params.row.supplier) return null;
    return (
      <Tooltip
        placement="top"
        variant="outlined"
        arrow
        title={
          params.row.supplier ? (
            <SupplierTooltip supplier={params.row.supplier} />
          ) : null
        }
      >
        <Chip size={"sm"} sx={{ borderRadius: 4 }}>
          {params.row.supplier?.name}
        </Chip>
      </Tooltip>
    );
  }

  function SupplierEditComponent(props: GridRenderEditCellParams) {
    const { id, value, field, hasFocus } = props;
    const apiRef = useGridApiContext();

    return (
      <SupplierAutocomplete
        hasFocus={hasFocus}
        teamId={team.id}
        supplier={value}
        onChange={(supplier) => {
          apiRef.current.setEditCellValue({
            id,
            field,
            value: {
              ...supplier,
            },
          });
          apiRef.current.stopCellEditMode({ id, field });
        }}
      />
    );
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "ref",
        headerName: "ID",
        width: 80,
        editable: false,
        renderCell: RefRenderCell,
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        editable: true,
        renderCell: StatusRenderCell,
        // renderEditCell: StatusRenderEditCell,
        type: "singleSelect",
        valueOptions: [
          "DRAFT",
          "PENDING",
          "CONFIRMED",
          "CANCELLED",
          "INPROGRESS",
          "COMPLETED",
        ],
      },
      {
        field: "serviceType",
        headerName: "Service Type",
        width: 120,
        editable: true,
        type: "singleSelect",
        renderCell: ServiceTypeRenderCell,
        valueOptions: [PSIValue, InlineValue, FAValue],
      },
      team.kind === TeamKind.Brand
        ? {
            field: "agency",
            headerName: "Agency",
            width: 200,
            editable: true,
            renderCell: AgencyRenderCell,
            renderEditCell: AgencyEditCell,
          }
        : {
            field: "brand",
            headerName: "Brand",
            width: 200,
            editable: true,
            renderCell: BrandRenderCell,
          },
      {
        field: "result",
        headerName: "Result",
        width: 120,
        editable: false,
        renderCell: ResultRenderCell,
      },
      {
        field: "supplier",
        headerName: "Supplier",
        width: 200,
        editable: true,
        renderCell: SupplierRenderCell,
        renderEditCell: SupplierEditComponent,
      },
      {
        field: "startDate",
        type: "date",
        valueGetter: (value: string | null) => {
          if (!value) return value;
          return new Date(value);
        },
        headerName: "Start Date",
        width: 120,
        editable: true,
      },
      {
        field: "endDate",
        type: "date",
        valueGetter: (value: string | null) => {
          if (!value) return value;
          return new Date(value);
        },
        headerName: "End Date",
        width: 120,
        editable: true,
      },

      {
        field: "serviceDays",
        headerName: "Service Days",
        width: 120,
        editable: true,
      },
      // {
      //   field: "visibility",
      //   headerName: "Visibility",
      //   width: 120,
      //   editable: true,
      //   renderCell: VisibilityRenderCell,
      //   // renderEditCell: StatusRenderEditCell,
      //   type: "singleSelect",
      //   valueOptions: [
      //     "PRIVATE", // Only visible to the creating brand.
      //     "PUBLIC", // Visible to all eligible agencies.
      //     "ASSIGNED",
      //   ],
      // },

      // {
      //   field: "quotes",
      //   headerName: "Quotes",
      //   width: 80,
      //   editable: false,
      //   renderCell: QuoteRenderCell,
      // },

      // {
      //   field: "samples",
      //   headerName: "Samples",
      //   width: 120,
      //   editable: true,
      //   renderCell: SamplesRenderCell,
      // },
    ],

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleProcessRowUpdate = useCallback(
    async (
      newRow: GridRowModel,
      oldRow: GridRowModel,
      { rowId }: { rowId: GridRowId }
    ) => {
      const delta = (field: string, key?: string) =>
        newRow[field] !== oldRow[field]
          ? typeof key === "string" && typeof newRow[field] === "object"
            ? newRow[field][key]
            : newRow[field]
          : void 0;

      await inspectionUpdateMutation({
        variables: {
          updates: {
            uniqueId: rowId as string,
            status: delta("status"),
            visibility: delta("visibility"),
            serviceType: delta("serviceType"),
            startDate: delta("startDate"),
          },
        },
      });

      return newRow;
    },
    [inspectionUpdateMutation]
  );

  return (
    <>
      <DataGrid
        {...GridStyles}
        loading={loading}
        rows={inspections}
        columns={columns}
        editMode="cell"
        getRowId={(row) => row.uniqueId}
        disableRowSelectionOnClick
        processRowUpdate={handleProcessRowUpdate}
      />
      {/* {quotesModalOpen && inspectionRef && (
        <QuotesModal
          inspectionRef={inspectionRef}
          open={quotesModalOpen}
          onClose={handleQuotesModalClose}
        />
      )} */}
    </>
  );
}
